<template>
  <div v-if="visible" class="member-selector-overlay" @click="handleOverlayClick">
    <div class="member-selector-container" @click.stop>
      <!-- 弹窗头部 -->
      <div class="selector-header">
        <h3>选择会员</h3>
        <button class="close-button" @click="close">×</button>
      </div>

      <!-- 搜索和操作区域 -->
      <div class="selector-toolbar">
        <div class="search-bar">
          <input
            v-model="searchKeyword"
            type="text"
            placeholder="搜索会员姓名或手机号..."
            class="search-input"
            @keyup.enter="handleSearch"
          />
          <button class="search-button" @click="handleSearch">搜索</button>
        </div>
        <button class="add-member-button" @click="openAddMemberForm">
          + 新增会员
        </button>
      </div>

      <!-- 会员列表 -->
      <div class="members-list">
        <div v-if="loading" class="loading">
          加载中...
        </div>
        <div v-else-if="filteredMembers.length === 0" class="no-data">
          {{ searchKeyword ? '未找到匹配的会员' : '暂无会员数据' }}
        </div>
        <div
          v-for="member in filteredMembers"
          :key="member.id"
          class="member-item"
          :class="{ selected: selectedMember && selectedMember.id === member.id }"
          @click="selectMember(member)"
        >
          <div class="member-info">
            <div class="member-name">{{ member.name }}</div>
            <div class="member-phone">{{ member.phone }}</div>
            <div class="member-details">
              <span class="gender">{{ member.gender || '未知' }}</span>
              <span class="address">{{ member.address || '无地址' }}</span>
            </div>
          </div>
          <div class="member-actions">
            <button class="select-btn" @click.stop="confirmSelect(member)">
              选择
            </button>
            <button class="edit-btn" @click.stop="openEditMemberForm(member)">
              修改
            </button>
          </div>
        </div>
      </div>

      <!-- 底部操作 -->
      <div class="selector-footer">
        <button class="cancel-btn" @click="close">取消</button>
        <button
          class="confirm-btn"
          :disabled="!selectedMember"
          @click="confirmSelect(selectedMember)"
        >
          确定选择
        </button>
      </div>
    </div>

    <!-- 会员表单弹窗 -->
    <MemberForm
      :visible="showMemberForm"
      :member="editingMember"
      @close="closeMemberForm"
      @submit="handleMemberSubmit"
    />
  </div>
</template>

<script>
import MemberForm from './MemberForm.vue'
import { memberApi } from '../api/index.js'

export default {
  name: 'MemberSelector',
  components: {
    MemberForm
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close', 'select'],
  data() {
    return {
      searchKeyword: '',
      selectedMember: null,
      showMemberForm: false,
      editingMember: null,
      members: [],
      loading: false
    }
  },
  computed: {
    filteredMembers() {
      if (!this.searchKeyword.trim()) {
        return this.members
      }

      const keyword = this.searchKeyword.toLowerCase()
      return this.members.filter(member =>
        member.name.toLowerCase().includes(keyword) ||
        member.phone.includes(keyword)
      )
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.resetState()
        this.loadMembers()
      }
    }
  },
  methods: {
    resetState() {
      this.searchKeyword = ''
      this.selectedMember = null
      this.showMemberForm = false
      this.editingMember = null
    },

    // 加载会员列表
    async loadMembers() {
      this.loading = true
      try {
        const result = await memberApi.getMemberList({
          page: 1,
          pageSize: 100 // 临时方案，获取前100条数据
        })

        if (result.code === 0 && result.data && result.data.list) {
          // 转换数据格式，将后端的gender映射为前端显示格式
          this.members = result.data.list.map(member => ({
            id: member.ID,
            name: member.name,
            phone: member.phone,
            gender: this.mapGenderToChinese(member.gender),
            address: member.address || '无地址'
          }))
          console.log('会员列表加载成功:', this.members)
        } else {
          console.error('获取会员列表失败:', result.msg)
          alert(result.msg || '获取会员列表失败')
        }
      } catch (error) {
        console.error('获取会员列表异常:', error)
        alert('获取会员列表失败，请检查网络连接')
      } finally {
        this.loading = false
      }
    },

    // 性别映射：后端字段 -> 前端显示
    mapGenderToChinese(gender) {
      const genderMap = {
        'male': '男',
        'female': '女',
        'other': '未知'
      }
      return genderMap[gender] || '未知'
    },

    // 性别映射：前端显示 -> 后端字段
    mapGenderToEnglish(gender) {
      const genderMap = {
        '男': 'male',
        '女': 'female',
        '未知': 'other',
        '': 'other'
      }
      return genderMap[gender] || 'other'
    },

    handleSearch() {
      // 搜索结果通过computed属性自动更新
      if (!this.searchKeyword.trim()) {
        return
      }

      const results = this.filteredMembers
      if (results.length === 0) {
        alert('未找到匹配的会员')
      } else {
        alert(`找到 ${results.length} 个会员`)
      }
    },

    selectMember(member) {
      this.selectedMember = member
    },

    confirmSelect(member) {
      if (!member) return

      this.$emit('select', member)
      this.close()
    },

    openAddMemberForm() {
      this.editingMember = null
      this.showMemberForm = true
    },

    openEditMemberForm(member) {
      this.editingMember = member
      this.showMemberForm = true
    },

    closeMemberForm() {
      this.showMemberForm = false
      this.editingMember = null
    },

    async handleMemberSubmit(memberData) {
      try {
        const submitData = {
          ...memberData,
          gender: this.mapGenderToEnglish(memberData.gender)
        }

        if (this.editingMember) {
          // 修改会员
          const result = await memberApi.updateMember(submitData)
          if (result.code === 0) {
            alert('会员信息更新成功')
            await this.loadMembers() // 重新加载列表
          } else {
            alert(result.msg || '更新会员失败')
          }
        } else {
          // 添加会员
          const result = await memberApi.addMember(submitData)
          if (result.code === 0) {
            alert('会员添加成功')
            await this.loadMembers() // 重新加载列表
          } else {
            alert(result.msg || '添加会员失败')
          }
        }
      } catch (error) {
        console.error('保存会员失败:', error)
        alert('保存失败，请检查网络连接')
      }
    },

    close() {
      this.$emit('close')
    },

    handleOverlayClick() {
      this.close()
    }
  }
}
</script>

<style scoped>
.member-selector-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.member-selector-container {
  background-color: white;
  border-radius: 12px;
  width: 90%;
  max-width: 700px;
  max-height: 80vh;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
}

.selector-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #f0f0f0;
  flex-shrink: 0;
}

.selector-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.close-button {
  background: none;
  border: none;
  font-size: 24px;
  color: #999;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: all 0.2s;
}

.close-button:hover {
  background-color: #f5f5f5;
  color: #666;
}

.selector-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid #f0f0f0;
  flex-shrink: 0;
}

.search-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  max-width: 400px;
}

.search-input {
  flex: 1;
  height: 36px;
  padding: 0 12px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  font-size: 14px;
  outline: none;
  transition: all 0.2s;
}

.search-input:focus {
  border-color: #1890ff;
  box-shadow: 0 2px 8px rgba(24, 144, 255, 0.1);
}

.search-button, .add-member-button {
  height: 36px;
  padding: 0 16px;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.search-button {
  background-color: #1890ff;
  color: white;
}

.search-button:hover {
  background-color: #4096ff;
}

.add-member-button {
  background-color: #52c41a;
  color: white;
  margin-left: 16px;
}

.add-member-button:hover {
  background-color: #73d13d;
}

.members-list {
  flex: 1;
  overflow-y: auto;
  padding: 16px 24px;
}

.member-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  margin-bottom: 12px;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.member-item:hover {
  border-color: #1890ff;
  box-shadow: 0 2px 8px rgba(24, 144, 255, 0.1);
}

.member-item.selected {
  border-color: #1890ff;
  background-color: #e6f7ff;
}

.member-info {
  flex: 1;
}

.member-name {
  font-size: 16px;
  font-weight: 500;
  color: #333;
  margin-bottom: 4px;
}

.member-phone {
  font-size: 14px;
  color: #666;
  margin-bottom: 6px;
  font-family: monospace;
}

.member-details {
  display: flex;
  gap: 16px;
  font-size: 12px;
  color: #999;
}

.gender {
  color: #666;
}

.address {
  color: #999;
}

.member-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.select-btn, .edit-btn {
  height: 32px;
  padding: 0 12px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid;
}

.select-btn {
  background-color: #1890ff;
  color: white;
  border-color: #1890ff;
}

.select-btn:hover {
  background-color: #4096ff;
  border-color: #4096ff;
}

.edit-btn {
  background-color: white;
  color: #1890ff;
  border-color: #1890ff;
}

.edit-btn:hover {
  background-color: #e6f7ff;
}

.loading {
  text-align: center;
  color: #999;
  padding: 40px;
  font-size: 14px;
}

.no-data {
  text-align: center;
  color: #999;
  padding: 40px;
  font-size: 14px;
}

.selector-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid #f0f0f0;
  flex-shrink: 0;
}

.cancel-btn, .confirm-btn {
  height: 36px;
  padding: 0 20px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid;
}

.cancel-btn {
  background-color: white;
  color: #666;
  border-color: #d9d9d9;
}

.cancel-btn:hover {
  border-color: #1890ff;
  color: #1890ff;
}

.confirm-btn {
  background-color: #1890ff;
  color: white;
  border-color: #1890ff;
}

.confirm-btn:hover:not(:disabled) {
  background-color: #4096ff;
  border-color: #4096ff;
}

.confirm-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* 动画效果 */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.member-selector-container {
  animation: fadeIn 0.2s ease-out;
}
</style>